import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "./client";
import type * as T from "./types";

export const qk = {
  session: ["session"] as const,
  orgs: ["orgs"] as const,
  // Os endereços dos termos mudam com o idioma (Accept-Language).
  trialInfo: (locale: string) => ["trialInfo", locale] as const,
  overview: (org: string) => ["org", org, "overview"] as const,
  machines: (org: string, f?: object) => ["org", org, "machines", f ?? {}] as const,
  machine: (org: string, id: string) => ["org", org, "machine", id] as const,
  snapshots: (org: string, id: string) => ["org", org, "snapshots", id] as const,
  disks: (org: string, id: string) => ["org", org, "disks", id] as const,
  databases: (org: string, id: string) => ["org", org, "databases", id] as const,
  foundDatabases: (org: string, id: string) => ["org", org, "foundDatabases", id] as const,
  shares: (org: string, id: string) => ["org", org, "shares", id] as const,
  credentials: (org: string) => ["org", org, "credentials"] as const,
  files: (org: string, snap: string, path: string) => ["org", org, "files", snap, path] as const,
  catalogSearch: (org: string, id: string, q: string) => ["org", org, "catalogSearch", id, q] as const,
  catalogOccurrences: (org: string, id: string, dir: string, name: string) => ["org", org, "catalogOccurrences", id, dir, name] as const,
  groups: (org: string) => ["org", org, "groups"] as const,
  policies: (org: string) => ["org", org, "policies"] as const,
  storage: (org: string) => ["org", org, "storage"] as const,
  usage: (org: string) => ["org", org, "usage"] as const,
  adminPools: ["admin", "pools"] as const,
  adminOrgPools: (org: string) => ["admin", "orgPools", org] as const,
  tokens: (org: string) => ["org", org, "tokens"] as const,
  activity: (org: string, m?: string) => ["org", org, "activity", m ?? ""] as const,
  audit: (org: string) => ["org", org, "audit"] as const,
  reportKinds: (org: string) => ["org", org, "reportKinds"] as const,
  issuedReports: (org: string) => ["org", org, "issuedReports"] as const,
  users: (org: string) => ["org", org, "users"] as const,
  apiTokens: (org: string) => ["org", org, "apiTokens"] as const,
  invitations: (org: string) => ["org", org, "invitations"] as const,
  alerts: (org: string, status: string) => ["org", org, "alerts", status] as const,
  channels: (org: string) => ["org", org, "notificationChannels"] as const,
};

export const useSession = () => {
  const api = useApi();
  return useQuery({ queryKey: qk.session, queryFn: () => api.getSession(), retry: false, staleTime: Infinity });
};
export const useOrgs = () => {
  const api = useApi();
  return useQuery({ queryKey: qk.orgs, queryFn: () => api.listOrganizations() });
};
export const useOverview = (org: string) => {
  const api = useApi();
  return useQuery({
    queryKey: qk.overview(org),
    queryFn: () => api.getOverview(org),
    refetchInterval: (q) => activeRefetch(q.state.data?.recentJobs),
  });
};
export const useMachines = (org: string, filter?: { status?: T.ProtectionStatus; groupId?: string; q?: string }) => {
  const api = useApi();
  return useQuery({ queryKey: qk.machines(org, filter), queryFn: () => api.listMachines(org, filter), placeholderData: (prev) => prev });
};
export const useMachine = (org: string, id: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.machine(org, id), queryFn: () => api.getMachine(org, id), refetchInterval: 15_000 });
};
export const useSnapshots = (org: string, id: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.snapshots(org, id), queryFn: () => api.listSnapshots(org, id) });
};
/** Discos da máquina para o backup de imagem (ADR 0022). */
export const useDisks = (org: string, id: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.disks(org, id), queryFn: () => api.listDisks(org, id) });
};
/** Instâncias de banco da máquina (ADR 0023). Com teste ou backup em curso, confere de novo em poucos segundos. */
export const useDatabases = (org: string, id: string) => {
  const api = useApi();
  return useQuery({
    queryKey: qk.databases(org, id),
    queryFn: () => api.listDatabases(org, id),
    refetchInterval: (q) => (q.state.data?.some((d) => d.activeJob !== "") ? 2_500 : false),
  });
};
export const useFoundDatabases = (org: string, id: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.foundDatabases(org, id), queryFn: () => api.foundDatabases(org, id) });
};
/** Compartilhamentos SMB da máquina (ADR 0030); com teste ou backup em curso, confere de novo em poucos segundos. */
export const useShares = (org: string, id: string) => {
  const api = useApi();
  return useQuery({
    queryKey: qk.shares(org, id),
    queryFn: () => api.listShares(org, id),
    refetchInterval: (q) => (q.state.data?.some((s) => s.activeJob !== "") ? 2_500 : false),
  });
};
/** Catálogo de credenciais (ADR 0030): só administrador humano; enabled desliga para os demais. */
export const useCredentials = (org: string, enabled = true) => {
  const api = useApi();
  return useQuery({ queryKey: qk.credentials(org), queryFn: () => api.listCredentials(org), enabled });
};
export const useFiles = (org: string, snap: string | undefined, path: string | undefined) => {
  const api = useApi();
  return useQuery({
    queryKey: qk.files(org, snap ?? "", path ?? ""),
    queryFn: () => api.browseSnapshot(org, snap ?? "", path ?? ""),
    // Caminho vazio é a raiz do ponto de restauração, e não "sem caminho":
    // exigi-lo aqui deixava a primeira tela de arquivos carregando para sempre.
    enabled: Boolean(snap),
  });
};
/** Busca no catálogo (ADR 0036); termo vazio não consulta. */
export const useCatalogSearch = (org: string, id: string, q: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.catalogSearch(org, id, q), queryFn: () => api.searchCatalog(org, id, q), enabled: q.trim() !== "" });
};
export const useCatalogOccurrences = (org: string, id: string, dir: string | undefined, name: string | undefined) => {
  const api = useApi();
  return useQuery({
    queryKey: qk.catalogOccurrences(org, id, dir ?? "", name ?? ""),
    queryFn: () => api.catalogOccurrences(org, id, dir ?? "", name ?? ""),
    enabled: Boolean(dir && name),
  });
};
export const useGroups = (org: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.groups(org), queryFn: () => api.listGroups(org) });
};
export const usePolicies = (org: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.policies(org), queryFn: () => api.listPolicies(org) });
};
/** Armazenamento contratado da organização, sem infraestrutura (ADR 0021). */
export const useStorage = (org: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.storage(org), queryFn: () => api.storage(org) });
};
export const useAdminPools = () => {
  const api = useApi();
  return useQuery({ queryKey: qk.adminPools, queryFn: () => api.adminListPools() });
};
export const useAdminOrgPools = (org: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.adminOrgPools(org), queryFn: () => api.adminListOrgPools(org), enabled: org !== "" });
};
export const useUsage = (org: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.usage(org), queryFn: () => api.getUsage(org) });
};
export const useTokens = (org: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.tokens(org), queryFn: () => api.listEnrollmentTokens(org) });
};
const activeRefetch = (jobs: T.Job[] | undefined) => (jobs?.some((j) => j.status === "queued" || j.status === "running") ? 4000 : 30_000);

export const useActivity = (org: string, machineId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: qk.activity(org, machineId),
    queryFn: () => api.listActivity(org, machineId),
    refetchInterval: (q) => activeRefetch(q.state.data),
  });
};
export const useAudit = (org: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.audit(org), queryFn: () => api.listAudit(org) });
};
export const useUsers = (org: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.users(org), queryFn: () => api.listUsers(org) });
};
export const useApiTokens = (org: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.apiTokens(org), queryFn: () => api.listApiTokens(org) });
};

/** Invalida tudo que pertence à organização após uma mutação. */
export const useInvitations = (org: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.invitations(org), queryFn: () => api.listInvitations(org) });
};

export const useAlerts = (org: string, status: "open" | "all" = "open") => {
  const api = useApi();
  return useQuery({ queryKey: qk.alerts(org, status), queryFn: () => api.listAlerts(org, status), refetchInterval: 60_000 });
};

export const useNotificationChannels = (org: string, enabled = true) => {
  const api = useApi();
  return useQuery({ queryKey: qk.channels(org), queryFn: () => api.listNotificationChannels(org), enabled });
};

/** Relatórios de evidência (ADR 0026). */
export const useReportKinds = (org: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.reportKinds(org), queryFn: () => api.listReportKinds(org), staleTime: Infinity });
};
export const useIssuedReports = (org: string) => {
  const api = useApi();
  return useQuery({ queryKey: qk.issuedReports(org), queryFn: () => api.listIssuedReports(org) });
};
export function useInvalidateOrg() {
  const qc = useQueryClient();
  return (org: string) => qc.invalidateQueries({ queryKey: ["org", org] });
}

/** Invalida pools, atribuições e o que o cliente vê depois de uma mudança do administrador. */
export function useInvalidateAdmin() {
  const qc = useQueryClient();
  return (org?: string) => {
    void qc.invalidateQueries({ queryKey: ["admin"] });
    if (org) void qc.invalidateQueries({ queryKey: ["org", org] });
  };
}

export { useMutation };
